import { Component } from '@theme/component';

/**
 * MLP Shop the Look gallery.
 *
 * A horizontal, scroll-snapping slider of "looks". Two looks sit side by side
 * (50/50, `data-mode="static"`); three or more become a looping carousel
 * (`data-mode="carousel"`) with one look centred on desktop (left-aligned on
 * mobile) and the neighbouring looks peeking on either side.
 *
 * The loop is seamless: clones of the first and last looks are inserted at the
 * ends so a peeking look is always present (never empty padding), and the
 * scroll position is silently reset when it settles on a clone. The track is a
 * native overflow scroller, so swipe / drag / trackpad work without JS — the
 * component layers prev/next, a live counter and the loop on top, and degrades
 * to a plain scroller when JS is absent.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} [track] - The scrolling track.
 * @property {HTMLElement[]} [looks] - Real look slides (clones are excluded).
 * @property {HTMLElement} [counter] - Live "n/total" counter.
 *
 * @extends {Component<Refs>}
 */
class ShopTheLookGallery extends Component {
  #current = 0;
  /** Index of the centred slide within the track (clones included). */
  #pos = 0;
  /** Number of leading clones (0 when not looping). */
  #offset = 0;
  #looping = false;
  /** @type {number | null} */
  #rafId = null;
  /** @type {number | null} */
  #pageScrollId = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #idleTimer = null;

  connectedCallback() {
    super.connectedCallback();
    this.#buildLoop();
    this.refs.track?.addEventListener('scroll', this.#onScroll, { passive: true });
    this.refs.track?.addEventListener('scrollend', this.#normalise);
    window.addEventListener('resize', this.#onResize, { passive: true });
    window.addEventListener('scroll', this.#onPageScroll, { passive: true });
    requestAnimationFrame(() => this.#scrollToPos(this.#pos, 'instant'));
    this.#updateStickyThumbs();
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.track?.removeEventListener('scroll', this.#onScroll);
    this.refs.track?.removeEventListener('scrollend', this.#normalise);
    window.removeEventListener('resize', this.#onResize);
    window.removeEventListener('scroll', this.#onPageScroll);
    if (this.#rafId != null) cancelAnimationFrame(this.#rafId);
    if (this.#pageScrollId != null) cancelAnimationFrame(this.#pageScrollId);
    if (this.#idleTimer != null) clearTimeout(this.#idleTimer);
  }

  /** @returns {HTMLElement[]} */
  get looks() {
    return /** @type {HTMLElement[]} */ (this.refs.looks ?? []);
  }

  get total() {
    return this.looks.length;
  }

  /** @returns {HTMLElement[]} All track children, clones included, in DOM order. */
  get #slides() {
    return /** @type {HTMLElement[]} */ (Array.from(this.refs.track?.children ?? []));
  }

  /** Looks centre on desktop; left-align on mobile. */
  get #centered() {
    return window.matchMedia('(min-width: 722px)').matches;
  }

  get #reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** Insert clones of the first/last looks so the loop has no empty edges. */
  #buildLoop() {
    const track = this.refs.track;
    const looks = this.looks;
    const n = looks.length;
    if (!track || this.dataset.mode !== 'carousel' || n < 2) return;

    // Guard against a second build (reconnect): recover the leading-clone count
    // instead of duplicating clones.
    if (track.querySelector('[data-clone]')) {
      let leading = 0;
      for (const child of track.children) {
        if (child.hasAttribute('data-clone') && leading === [...track.children].indexOf(child)) leading++;
        else break;
      }
      this.#offset = leading || 1;
      this.#pos = this.#offset;
      this.#looping = true;
      return;
    }

    // Clone the last/first `k` looks at each end so a wrapped look always has
    // its neighbour peeking — the silent reposition onto the real look is then
    // invisible (no empty peek = no "snap back to 1"). Two is enough because a
    // peek is always narrower than one look.
    const k = Math.min(2, n);
    for (let i = 0; i < k; i++) {
      track.insertBefore(this.#cloneLook(looks[n - k + i]), looks[0]); // leading: last k looks, in order
      track.append(this.#cloneLook(looks[i])); // trailing: first k looks, in order
    }

    this.#offset = k;
    this.#pos = this.#offset; // first real look
    this.#looping = true;
  }

  /** @param {HTMLElement} look @returns {HTMLElement} */
  #cloneLook(look) {
    const clone = /** @type {HTMLElement} */ (look.cloneNode(true));
    clone.removeAttribute('ref'); // keep clones out of refs.looks
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('data-clone', '');
    clone.inert = true; // not focusable, not announced
    clone.querySelectorAll('a').forEach((a) => a.setAttribute('tabindex', '-1'));
    clone.querySelectorAll('video').forEach((v) => v.removeAttribute('autoplay')); // don't double-play
    return clone;
  }

  next() {
    if (this.total < 2) return;
    this.#move(1);
  }

  previous() {
    if (this.total < 2) return;
    this.#move(-1);
  }

  /** @param {number} delta */
  #move(delta) {
    const target = this.#looping ? this.#pos + delta : this.#clamp(this.#pos + delta);
    this.#pos = target;
    this.#current = this.#realIndex(target);
    this.#update();
    this.#scrollToPos(target, this.#reduceMotion ? 'instant' : 'smooth');
  }

  /** @param {number} pos */
  #clamp(pos) {
    return Math.max(this.#offset, Math.min(pos, this.#offset + this.total - 1));
  }

  /** Maps a track position (clones included) to a real look index. @param {number} pos */
  #realIndex(pos) {
    const n = this.total;
    return (((pos - this.#offset) % n) + n) % n;
  }

  /** @param {number} pos @param {ScrollBehavior} behavior */
  #scrollToPos(pos, behavior) {
    const track = this.refs.track;
    const slide = this.#slides[pos];
    if (!track || !slide) return;

    const left = this.#centered
      ? slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2
      : slide.offsetLeft - parseFloat(getComputedStyle(track).paddingInlineStart || '0');

    track.scrollTo({ left: Math.max(0, left), behavior });
  }

  /** The track position whose snap point is nearest the current scroll. */
  #nearestPos() {
    const track = this.refs.track;
    if (!track) return this.#pos;

    const lead = this.#centered ? track.clientWidth / 2 : parseFloat(getComputedStyle(track).paddingInlineStart || '0');
    const anchor = track.scrollLeft + lead;
    let best = this.#pos;
    let bestDistance = Infinity;

    this.#slides.forEach((slide, i) => {
      const point = this.#centered ? slide.offsetLeft + slide.clientWidth / 2 : slide.offsetLeft;
      const distance = Math.abs(point - anchor);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    });

    return best;
  }

  /** Keep the counter live while the user swipes / drags. */
  #onScroll = () => {
    // Fallback when `scrollend` is unsupported: normalise once the scroll idles.
    // Reset on every scroll tick so it only fires after motion stops.
    if (!('onscrollend' in window)) {
      if (this.#idleTimer != null) clearTimeout(this.#idleTimer);
      this.#idleTimer = setTimeout(this.#normalise, 150);
    }

    if (this.#rafId != null) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      const current = this.#realIndex(this.#nearestPos());
      if (current !== this.#current) {
        this.#current = current;
        this.#update();
      }
    });
  };

  /** When the scroll settles on a clone, jump to the matching real look. */
  #normalise = () => {
    if (!this.#looping) {
      this.#pos = this.#nearestPos();
      this.#current = this.#realIndex(this.#pos);
      this.#update();
      return;
    }

    const pos = this.#nearestPos();
    this.#current = this.#realIndex(pos);

    // The real slide showing the same look as `pos`. If we settled on a clone,
    // jump there instantly so the next swipe/step continues the loop.
    const realPos = this.#current + this.#offset;
    if (pos !== realPos) {
      this.#pos = realPos;
      this.#scrollToPos(realPos, 'instant'); // MUST be instant — this is the seamless wrap jump
    } else {
      this.#pos = pos;
    }

    this.#update();
  };

  #onPageScroll = () => {
    if (this.#pageScrollId != null) return;
    this.#pageScrollId = requestAnimationFrame(() => {
      this.#pageScrollId = null;
      this.#updateStickyThumbs();
    });
  };

  /**
   * On mobile, translate the thumbnail strip downward as the look's frame
   * scrolls past the viewport top, clamped so the strip never leaves the image.
   * Cleared on desktop.
   */
  #updateStickyThumbs() {
    const isMobile = !window.matchMedia('(min-width: 722px)').matches;

    for (const slide of this.#slides) {
      const frame = /** @type {HTMLElement|null} */ (slide.querySelector('.mlp-shop-the-look__frame'));
      const thumbs = /** @type {HTMLElement|null} */ (slide.querySelector('[data-stl-thumbs]'));
      if (!frame || !thumbs) continue;

      if (!isMobile) {
        thumbs.style.transform = '';
        continue;
      }

      const frameH = frame.offsetHeight;
      const thumbsH = thumbs.offsetHeight;
      const maxTravel = Math.max(0, frameH - thumbsH);
      // How far the frame top has scrolled above the viewport top.
      const scrolledPast = Math.max(0, -frame.getBoundingClientRect().top);
      const travel = Math.min(scrolledPast, maxTravel);

      thumbs.style.transform = travel > 0 ? `translateY(${travel}px)` : '';
    }
  }

  #onResize = () => {
    this.#scrollToPos(this.#pos, 'instant');
    this.#updateStickyThumbs();
  };

  #update() {
    const counter = this.refs.counter;
    if (counter) counter.textContent = `${this.#current + 1}/${this.total}`;
    this.looks.forEach((look, i) => look.toggleAttribute('data-current', i === this.#current));
  }
}

if (!customElements.get('shop-the-look-gallery')) {
  customElements.define('shop-the-look-gallery', ShopTheLookGallery);
}
